import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  reportFrontendError,
} from '@/shared/monitoring/report-frontend-error'

type FrontendErrorBoundaryProps = {
  children: ReactNode
}

type FrontendErrorBoundaryState = {
  hasError: boolean
}

export class FrontendErrorBoundary extends Component<
  FrontendErrorBoundaryProps,
  FrontendErrorBoundaryState
> {
  state: FrontendErrorBoundaryState = {
    hasError: false,
  }

  static getDerivedStateFromError(): FrontendErrorBoundaryState {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportFrontendError('react-error-boundary', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    })
  }

  private readonly handleReload = () => {
    window.location.reload()
  }

  override render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 py-10 text-foreground">
        <div className="max-w-lg text-center">
          <p className="text-sm font-medium uppercase tracking-[0.24em] text-muted-foreground">
            Something went wrong
          </p>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight">
            The app hit an unexpected error.
          </h1>
          <p className="mt-3 text-sm leading-7 text-muted-foreground sm:text-base">
            We sent the error details to the backend so the team can investigate.
            You can reload the page and try again.
          </p>
          <Button className="mt-8" onClick={this.handleReload}>
            Reload app
          </Button>
        </div>
      </div>
    )
  }
}
